import { useState, useEffect, useRef, useCallback } from 'react';
import { StickyNote, X, Pin } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import TaskList from '@tiptap/extension-task-list';
import TaskItem from '@tiptap/extension-task-item';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FloatingEditor = ({ content, sectionName, onSave, onEditorReady }) => {
  const saveTimer = useRef(null);
  const lastSavedRef = useRef(content || '');

  const flush = useCallback(() => {
    if (saveTimer.current) {
      clearTimeout(saveTimer.current);
      saveTimer.current = null;
    }
  }, []);

  const editor = useEditor({
    extensions: [
      StarterKit,
      TaskList,
      TaskItem.configure({ nested: true }),
    ],
    content: content || '<ul data-type="taskList"><li data-type="taskItem" data-checked="false"><p></p></li></ul>',
    editorProps: {
      attributes: {
        class: 'outline-none min-h-[200px] max-h-[300px] overflow-y-auto px-3 py-2 text-sm text-slate-700 leading-relaxed prose prose-sm max-w-none [&_ul[data-type=taskList]]:pl-0 [&_li[data-type=taskItem]]:flex [&_li[data-type=taskItem]]:gap-2 [&_li[data-type=taskItem]]:items-start [&_li]:my-0.5 [&_p]:my-0',
      },
    },
    onUpdate: ({ editor: ed }) => {
      // Auto-convert plain text to task list — but only when the user has actually typed
      // something into a stray paragraph. Toggling a checkbox can fire onUpdate with the
      // selection sitting outside any taskItem, which previously created a phantom empty
      // checkbox at the bottom.
      const { $from } = ed.state.selection;
      let inTaskItem = false;
      for (let d = $from.depth; d >= 0; d--) {
        if ($from.node(d).type.name === 'taskItem') { inTaskItem = true; break; }
      }
      if (!inTaskItem && ed.isActive('paragraph')) {
        const currentParagraph = $from.parent;
        if (currentParagraph && currentParagraph.textContent.trim().length > 0) {
          ed.chain().focus().toggleTaskList().run();
        }
      }

      // Fire an immediate event so the Dashboard's Today's Focus tracker reflects
      // the new check-state instantly. The actual server save is still debounced below.
      window.dispatchEvent(new CustomEvent('todos:optimistic-update', {
        detail: { section: sectionName, html: ed.getHTML() }
      }));

      if (saveTimer.current) clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(() => {
        const html = ed.getHTML();
        lastSavedRef.current = html;
        onSave(sectionName, html);
      }, 800);
    },
  }, [sectionName]);

  useEffect(() => {
    if (editor && content !== undefined) {
      const current = editor.getHTML();
      if (current !== content) {
        editor.commands.setContent(content || '<ul data-type="taskList"><li data-type="taskItem" data-checked="false"><p></p></li></ul>');
        lastSavedRef.current = content || '';
      }
    }
  }, [sectionName]);

  // Expose a flush handle to the parent so closing the panel / switching section /
  // unmounting can immediately persist whatever the user just typed.
  useEffect(() => {
    if (!editor || !onEditorReady) return;
    onEditorReady({
      flushSave: () => {
        flush();
        if (!editor || editor.isDestroyed) return;
        const html = editor.getHTML();
        if (html !== lastSavedRef.current) {
          lastSavedRef.current = html;
          onSave(sectionName, html);
        }
      },
    });
    return () => {
      // On unmount: force a final flush so we don't lose the last 800ms of typing
      if (saveTimer.current) {
        clearTimeout(saveTimer.current);
        if (editor && !editor.isDestroyed) {
          const html = editor.getHTML();
          if (html !== lastSavedRef.current) onSave(sectionName, html);
        }
      }
    };
  }, [editor, sectionName, onSave, flush, onEditorReady]);

  if (!editor) return null;
  return <EditorContent editor={editor} />;
};

export const FloatingNote = () => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [sections, setSections] = useState([]);
  const [pinnedSection, setPinnedSection] = useState(null);
  const [content, setContent] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user) fetchData();
  }, [user]);

  const fetchData = async () => {
    try {
      const [secRes, pinnedRes] = await Promise.all([
        axios.get(`${API_URL}/api/todo-sections`, { withCredentials: true }),
        axios.get(`${API_URL}/api/todo-pinned`, { withCredentials: true }),
      ]);
      const allSections = secRes.data || [];
      setSections(allSections);
      // Default to last-used section, otherwise first
      const remembered = pinnedRes.data?.pinned_section;
      const exists = allSections.find(s => s.section === remembered);
      const active = exists ? remembered : (allSections[0]?.section || null);
      setPinnedSection(active);
      if (active) {
        const sec = allSections.find(s => s.section === active);
        setContent(sec?.content || '');
      }
    } catch {}
  };

  const editorHandle = useRef(null);

  const handleSave = useCallback(async (sectionName, html) => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/todo-sections/${encodeURIComponent(sectionName)}`, {
        content: html
      }, { withCredentials: true });
      // Let other surfaces (like the Dashboard Today's Focus card) refresh their progress
      window.dispatchEvent(new CustomEvent('todos:updated', { detail: { section: sectionName } }));
    } catch (err) {
      console.error('Quick Notes save failed:', err);
      toast.error('Quick Notes save failed — try once more', { duration: 3500 });
    }
    setSaving(false);
  }, []);

  const switchSection = (sectionName) => {
    // Flush any pending save before swapping out the editor so we never lose typing
    try { editorHandle.current?.flushSave?.(); } catch {}
    setPinnedSection(sectionName);
    const sec = sections.find(s => s.section === sectionName);
    setContent(sec?.content || '');
    axios.put(`${API_URL}/api/todo-pinned`, { section: sectionName }, { withCredentials: true }).catch(() => {});
  };

  const closePanel = () => {
    try { editorHandle.current?.flushSave?.(); } catch {}
    setOpen(false);
  };

  if (!user || !pinnedSection) return null;

  return (
    <>
      <button
        data-testid="floating-note-btn"
        onClick={() => {
          if (open) {
            closePanel();
          } else {
            setOpen(true);
            fetchData();
          }
        }}
        className="fixed bottom-20 right-6 z-40 w-12 h-12 rounded-full bg-amber-500 hover:bg-amber-600 text-white shadow-lg hover:shadow-xl transition-all flex items-center justify-center"
        title="Quick Notes"
      >
        {open ? <X size={20} /> : <StickyNote size={20} />}
      </button>

      {open && (
        <div
          data-testid="floating-note-panel"
          className="fixed bottom-[136px] right-6 z-40 w-80 md:w-96 bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden"
          style={{ maxHeight: 'calc(100vh - 200px)' }}
        >
          <div className="bg-gradient-to-r from-amber-500 to-amber-600 px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Pin size={14} className="text-white" />
              <span className="text-sm font-bold text-white">Quick Notes</span>
              {saving && <span className="text-[10px] text-amber-200">Saving...</span>}
            </div>
            <button onClick={closePanel} className="text-amber-200 hover:text-white">
              <X size={16} />
            </button>
          </div>

          {sections.length > 1 && (
            <div className="px-3 py-2 border-b border-slate-100 flex gap-1 overflow-x-auto">
              {sections.map(s => (
                <button
                  key={s.section}
                  onClick={() => switchSection(s.section)}
                  className={`text-[10px] font-semibold px-2.5 py-1 rounded-full whitespace-nowrap transition-colors ${
                    pinnedSection === s.section
                      ? 'bg-amber-500 text-white'
                      : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                  }`}
                >
                  {s.section}
                </button>
              ))}
            </div>
          )}

          <div className="p-0 bg-white rounded-b-2xl todo-clean todo-clean-sm">
            <FloatingEditor
              content={content}
              sectionName={pinnedSection}
              onSave={handleSave}
              onEditorReady={(handle) => { editorHandle.current = handle; }}
            />
          </div>
        </div>
      )}
    </>
  );
};
